// BCM · Goods Receipt Dashboard — shared Sync PO / Sync Inventory buttons
// (blue, as in the mockups). Builds the payloads with the shared hook and
// hands both success and failure off to the shared GRFeedbackContext — so the
// user sees the exact same green success banner and the Updating Batch toast
// as every other batch-mutating action on the dashboard.
//
// resetsBatch is set per pending action (V5); every caller passes true so the
// reset (A16 resetWorkflowFromScratch) runs after a successful sync. When
// `singleSync` is set only that one sync renders (PA8 per-check).
//
// onRefresh — required when any caller sets resetsBatch=true, so the shared
// Updating Batch poll can call it once the batch settles. Callers already
// receive onRefresh from ActionSection; just thread it through.
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, CircularProgress } from "@material-ui/core";
import { useGRStyles, BLUE } from "../../goodsReceiptStyles";
import useSyncWithRecheck from "../../hooks/useSyncWithRecheck";
import { getFeaturesAccess } from "../../../../../containers/actions/common/accessControl";

const SyncActions = ({
  row,
  detail,
  showSyncPO,
  showSyncInventory,
  resetsBatchPO = false,
  resetsBatchInventory = false,
  onRefresh,
  onSyncSuccess,
}) => {
  const classes = useGRStyles();
  const { runningType, runSync, buildPOWorkflowResetPayload, buildSyncInventoryPayload } =
    useSyncWithRecheck({ onRefresh });
  const runningPO = runningType === "PO";
  const runningInventory = runningType === "INVENTORY";

  const source = { ...(row || {}), ...(detail || {}) };

  const onSyncPO = () =>
    runSync({ type: "PO", payload: buildPOWorkflowResetPayload(source), resetsBatch: resetsBatchPO, source, onSyncSuccess });
  const onSyncInventory = () =>
    runSync({ type: "INVENTORY", payload: buildSyncInventoryPayload(source), resetsBatch: resetsBatchInventory, source, onSyncSuccess });

  // syncPO/syncInventory access is checked against its OWN dedicated
  // getFeaturesAccess call — the "fgBatch" screen / "fgBatchFeatures" set —
  // the exact same one the View Batch drawer (sasl/common/drawer/QuickView.jsx)
  // fetches and gates its own Sync PO / Sync Inventory buttons on
  // (sasl/batch/updateBatch/ViewBatchPageButtons.jsx). GR's dashboard-level
  // "deliverySchedulesFeatures" access is a different feature set and is not
  // used for this check. A missing/undefined flag means not granted, not
  // "allowed by default".
  const dispatch = useDispatch();
  const fgBatchScreenAccess = useSelector(
    (state) => state.pcmp.common.accessControl.screensAccess?.data?.fgBatch
  );
  const fgBatchFeatures = useSelector(
    (state) => state.pcmp.common.accessControl.featuresAccess?.fgBatchFeatures
  );
  useEffect(() => {
    if (fgBatchScreenAccess?.screenAccess && !fgBatchFeatures) {
      dispatch(getFeaturesAccess(fgBatchScreenAccess.screenId, "fgBatchFeatures"));
    }
  }, [dispatch, fgBatchScreenAccess, fgBatchFeatures]);

  const canSyncPO = !!showSyncPO && !!(fgBatchFeatures && fgBatchFeatures.syncPO);
  const canSyncInv = !!showSyncInventory && !!(fgBatchFeatures && fgBatchFeatures.syncInventory);
  if (!canSyncPO && !canSyncInv) return null;

  // Only the button actually syncing should look "loading" (spinner + the
  // dimmed disabled colour). The hook can only track one in-flight call, so
  // the OTHER button is still disabled to block a second concurrent sync,
  // but forced back to its normal blue via inline style so it doesn't also
  // read as loading — the `disabled` HTML attribute pseudo-class styling
  // from classes.blueBtn is overridden by this inline style.
  const running = runningType !== null;
  const idleDisabledStyle = { backgroundColor: BLUE, color: "#FFFFFF", opacity: 1 };

  return (
    <React.Fragment>
      {canSyncInv && (
        <Button
          className={classes.blueBtn}
          disableElevation
          disabled={running}
          style={running && !runningInventory ? idleDisabledStyle : undefined}
          onClick={onSyncInventory}
          startIcon={runningInventory ? <CircularProgress size={13} color="inherit" /> : null}
        >
          Sync Inventory
        </Button>
      )}
      {canSyncPO && (
        <Button
          className={classes.blueBtn}
          disableElevation
          disabled={running}
          style={running && !runningPO ? idleDisabledStyle : undefined}
          onClick={onSyncPO}
          startIcon={runningPO ? <CircularProgress size={13} color="inherit" /> : null}
        >
          Sync PO
        </Button>
      )}
    </React.Fragment>
  );
};

export default SyncActions;
